import { useEffect, useRef, useState } from "react";
import { Check, Languages } from "lucide-react";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { LANGUAGES, type LangCode } from "@/i18n/config";
import { useLanguage } from "@/i18n/LanguageContext";
import { pathForLocale, localeFromPath, toEnglishPath, hasMirror } from "@/lib/i18n-paths";
import { placeTranslationsQueryOptions } from "@/hooks/use-place-translations";
import { getUiOverrides } from "@/lib/ui-overrides.functions";
import { UI_EN } from "@/i18n/ui";

interface Props {
  variant?: "light" | "dark";
  align?: "left" | "right";
  drop?: "down" | "up";
}

/**
 * Compact language picker. `light` = navy text on glass (use in Header),
 * `dark` = white text on navy (use in Footer).
 *
 * EN/FI selections cross-navigate to the mirrored URL tree
 * (`/eat` ↔ `/fi/eat`) so search engines see distinct pages per locale.
 * Other UI languages just update the runtime translation context.
 */
export function LanguageSwitcher({ variant = "light", align = "right", drop = "down" }: Props) {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const queryClient = useQueryClient();
  const warmed = useRef(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const warmFi = () => {
    if (warmed.current) return;
    warmed.current = true;
    const englishPath = toEnglishPath(pathname);
    if (hasMirror(englishPath)) {
      const fiPath = pathForLocale(pathname, "fi");
      if (fiPath !== pathname) void router.preloadRoute({ to: fiPath as never }).catch(() => {});
    }
    void queryClient.prefetchQuery(placeTranslationsQueryOptions("fi"));
    void getUiOverrides({ data: { lang: "fi", sources: Array.from(new Set(Object.values(UI_EN))) } }).catch(() => {});
  };
  const urlLocale = localeFromPath(pathname);
  const effectiveLang: LangCode = urlLocale === "fi" ? "fi" : lang;
  const current = LANGUAGES.find((l) => l.code === effectiveLang)!;

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const handlePick = (code: LangCode) => {
    setLang(code);
    setOpen(false);
    // Only EN/FI have mirrored URL trees, and only for routes that actually
    // have a Finnish counterpart. Anywhere else we stay on the same URL and
    // let the runtime translation layer (with English fallback for any field
    // that has no Finnish value yet) handle the copy.
    const englishPath = toEnglishPath(pathname);
    if (code === "fi" && hasMirror(englishPath)) {
      const nextPath = pathForLocale(pathname, "fi");
      if (nextPath !== pathname) router.navigate({ to: nextPath, replace: false });
      return;
    }
    if (code !== "fi" && englishPath !== pathname) {
      // Leaving the Finnish tree — drop the /fi prefix.
      router.navigate({ to: englishPath, replace: false });
    }
  };

  const triggerCls =
    variant === "dark"
      ? "inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-white/15 bg-white/[0.04] hover:bg-white/10 hover:border-white/30 transition-colors text-xs text-white/85"
      : "h-9 px-3 inline-flex items-center gap-1.5 rounded-full text-navy/85 hover:bg-white hover:text-navy hover:shadow-sm transition-all text-xs font-semibold";

  const alignCls =
    align === "right"
      ? "left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-0"
      : "left-1/2 -translate-x-1/2 sm:translate-x-0 sm:left-0";
  const menuPos =
    drop === "up" ? `bottom-full mb-2 ${alignCls}` : `mt-3 ${alignCls}`;

  const menuCls =
    variant === "dark"
      ? `absolute z-30 ${menuPos} w-56 max-w-[calc(100vw-1.5rem)] max-h-72 overflow-y-auto rounded-2xl border border-white/15 bg-navy-deep/95 backdrop-blur-xl shadow-2xl p-1.5`
      : `absolute z-30 ${menuPos} w-56 max-w-[calc(100vw-1.5rem)] max-h-72 overflow-y-auto bg-white/95 backdrop-blur-2xl border border-white/70 rounded-2xl shadow-2xl shadow-navy/15 p-1.5 ring-1 ring-navy/5`;

  const itemBase = "w-full text-left px-3 py-2 text-sm rounded-xl flex items-center justify-between gap-3 transition-colors";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => { warmFi(); setOpen((o) => !o); }}
        onMouseEnter={warmFi}
        onFocus={warmFi}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("nav.chooseLanguage")}
        className={triggerCls}
      >
        <Languages className={variant === "dark" ? "size-3.5 text-white/60" : "size-4"} strokeWidth={2.25} />
        <span className="font-mono text-[11px] font-bold leading-none tracking-[0.08em]">{current.code.toUpperCase()}</span>
        <span className="hidden lg:inline">{current.label}</span>
      </button>
      {open && (
        <ul role="listbox" className={menuCls}>
          <li
            className={
              variant === "dark"
                ? "px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.3em] text-white/70"
                : "px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.3em] text-navy/45"
            }
          >
            {t("nav.chooseLanguage")}
          </li>
          {LANGUAGES.map((l) => {
            const active = l.code === effectiveLang;
            const activeCls =
              variant === "dark"
                ? active ? "bg-teal/15 text-teal" : "text-white/80 hover:bg-white/5 hover:text-white"
                : active ? "bg-teal/10 text-teal font-semibold" : "text-navy hover:bg-navy/5";
            return (
              <li key={l.code}>
                <button
                  type="button"
                  onClick={() => handlePick(l.code)}
                  role="option"
                  aria-selected={active}
                  className={`${itemBase} ${activeCls}`}
                >
                  <span className="flex items-center gap-2.5 min-w-0">
                    <span className="w-6 shrink-0 font-mono text-[11px] font-bold leading-none tracking-[0.08em]">{l.code.toUpperCase()}</span>
                    <span className="truncate">{l.label}</span>
                  </span>
                  {active && <Check className="size-3.5 shrink-0" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
